export const CATEGORY_LOCALE = 'en'
export const HOME_PATH = '/'
export const EXCLUSIVE_OFFERS_SLUG = 'exclusive-offers'
export const EXCLUSIVE_OFFERS_CATEGORY = 'B_CLASS'
export const USER_INFO_PATH = '/user-info'
export const COUPON_INDEX_PATH = '/coupon-index'
export const LOGIN_PATH = '/login'
export const CART_PATH = '/cart'
export const ALL_PRODUCTS_PATH = '/all-products'
export const NOT_FOUND_PATH = '/404'

export function withLocale(path = '/') {
  if (path == null || path === '') return '/'
  const normalized = String(path)
  return normalized.startsWith('/') ? normalized : `/${normalized}`
}

export function catalogPath(path = '/') {
  const normalized = !path || path === '/' ? '' : String(path)
  if (!normalized) return `/${CATEGORY_LOCALE}`
  if (normalized === `/${CATEGORY_LOCALE}` || normalized.startsWith(`/${CATEGORY_LOCALE}/`)) return normalized
  return `/${CATEGORY_LOCALE}${normalized.startsWith('/') ? normalized : `/${normalized}`}`
}

export function getOrderReviewLocation(orderId) {
  if (!orderId) return { path: USER_INFO_PATH }
  return { path: `/order/review/orderId/${orderId}` }
}

export const RESERVED_CATEGORY_SLUGS = new Set([
  'product',
  'products',
  'productlist',
  'exclusive-offers',
  'cart',
  'login',
  'loginother',
  'otherlogin',
  'register',
  'register2',
  'privacypolicy',
  'forgotpassword',
  'resetpassword',
  'blogs',
  'blog',
  'userinfo',
  'user-info',
  'userinformation',
  'couponindex',
  'coupon-index',
  'userwallet',
  'withdraw',
  'contactus',
  'coin-list',
  'settlement',
  'oldsettlement',
  'createorder',
  'order',
  'ordermain',
  'successful',
  'other-payments',
  'aftersale',
  'pricoo-special',
  'commitments',
  'availableoffers',
  'aboutpricoo',
  '404',
  'ocr',
  'orderinvoice',
  'series',
  'zindagi',
  'congratulations',
  'lucky-draw',
  'luckydrawpage',
  'lucky-no',
  'winner-history',
  'user-history',
  'df-lucky-draw',
  'apply-df-lucky-draw',
  'df-lucky-no',
  'custom-home',
  'other',
  'en'
])

const KEEP_LOCALE_SLUGS = new Set(['products', 'exclusive-offers'])

export function canonicalizeAppPath(path) {
  let next = String(path || '/')
    .toLowerCase()
    .replace(/\/order\/review\/orderid\//, '/order/review/orderId/')
    .replace(/\/specid\//g, '/specId/')
    .replace(/\/productid\//g, '/productId/')

  if (next === '/en' || next === '/en/') return HOME_PATH

  if (next.startsWith('/en/')) {
    const l1 = next.slice(4).split('/').filter(Boolean)[0] || ''
    if (!KEEP_LOCALE_SLUGS.has(l1) && RESERVED_CATEGORY_SLUGS.has(l1)) {
      next = next.slice(3)
    }
  }

  if (next === '/userinfo') return USER_INFO_PATH
  if (next === '/couponindex') return COUPON_INDEX_PATH
  return next
}

export function slugifyName(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function getCategorySlug(node) {
  if (!node) return ''
  const categorySlug = String(node.categorySlug || '').toLowerCase()
  if (categorySlug) return categorySlug
  return slugifyName(node.name)
}

function matchesSlug(node, slug) {
  if (!node || !slug) return false
  return getCategorySlug(node) === String(slug).toLowerCase()
}

export function findCategoryTrail(nodes, id, trail = []) {
  if (id == null || id === '') return null
  for (const node of nodes || []) {
    const next = trail.concat(node)
    if (String(node.id) === String(id)) return next
    const found = findCategoryTrail(node.children, id, next)
    if (found) return found
  }
  return null
}

export function findCategoryTrailBySlugs(nodes, slugs) {
  const parts = (slugs || []).filter(Boolean)
  if (!parts.length) return null
  const main = (nodes || []).find((item) => matchesSlug(item, parts[0]))
  if (!main) return null
  if (parts.length === 1) return [main]

  const terminalSlug = parts[parts.length - 1]
  if (matchesSlug(main, terminalSlug)) return [main]

  const found = findCategoryTrailBySlug(main.children, terminalSlug, [main])
  return found
}

function findCategoryTrailBySlug(nodes, slug, trail = []) {
  for (const node of nodes || []) {
    const next = trail.concat(node)
    if (matchesSlug(node, slug)) return next
    const found = findCategoryTrailBySlug(node.children, slug, next)
    if (found) return found
  }
  return null
}

export function trailToLocation(trail) {
  if (!trail?.length) return null
  const mainSlug = getCategorySlug(trail[0])
  const lastSlug = getCategorySlug(trail[trail.length - 1])
  if (!mainSlug) return null
  if (trail.length === 1 || !lastSlug || lastSlug === mainSlug) {
    return { path: `/${CATEGORY_LOCALE}/${mainSlug}` }
  }
  return { path: `/${CATEGORY_LOCALE}/${mainSlug}/${lastSlug}` }
}

export function getCategoryLocation(menuData, id) {
  return trailToLocation(findCategoryTrail(menuData, id))
}

export function getLegacyCategoryId(query = {}) {
  return query.thirdCategoryId || query.secondCategoryId || query.firstCategoryId || query.categoryId
}

export function isLegacyCategoryListQuery(query = {}) {
  if (query.keyword || query.homeModeId || query.category || query.couponType || query.productIds) return false
  return Boolean(getLegacyCategoryId(query))
}

export function isExclusiveOffersQuery(query = {}) {
  return String(query.category || '') === EXCLUSIVE_OFFERS_CATEGORY
}

export function getExclusiveOffersLocation(query = {}) {
  const { category, ...rest } = query
  return {
    path: `/${CATEGORY_LOCALE}/${EXCLUSIVE_OFFERS_SLUG}`,
    query: rest
  }
}

export function getCategoryLocationFromQuery(menuData, query = {}) {
  return getCategoryLocation(menuData, getLegacyCategoryId(query))
}

export function getCategoryQueryFromTrail(trail) {
  if (!trail?.length) return {}
  const last = trail[trail.length - 1]
  const level = Number(last.level) || trail.length
  if (level === 1) return { firstCategoryId: last.id }
  if (level === 2) return { secondCategoryId: last.id }
  return { thirdCategoryId: last.id }
}

export function navigateToCategory(router, menuData, id, extras = {}) {
  const loc = getCategoryLocation(menuData, id)
  if (loc) return router.push({ ...loc, ...extras })
  return router.push({
    name: 'productList',
    query: { firstCategoryId: id }
  })
}
